import React, { useState, useCallback, useRef, useEffect } from "react";
import ReactFlow, {
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Handle,
} from "reactflow";
import "reactflow/dist/style.css";

const CustomNode = ({ id, data }) => {
  const [expanded, setExpanded] = useState(false);
  const inputRefs = useRef({});

  const handleInputChange = (inputKey, value) => {
    data.input[inputKey].value = { fixedValue: value };
  };

  return (
    <div
      className="custom-node"
      style={{
        border: "1px solid #ddd",
        padding: "10px",
        borderRadius: "5px",
        background: "#f0f0f0",
        minWidth: "250px",
      }}
    >
      <div
        className="node-header"
        style={{ fontWeight: "bold", marginBottom: "5px" }}
      >
        <span>{data.id}</span>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{ marginLeft: "10px" }}
        >
          {expanded ? "Collapse" : "Expand"}
        </button>
      </div>
      {expanded && (
        <div className="node-content">
          <div className="input-fields" style={{ marginBottom: "10px" }}>
            <h4>Inputs</h4>
            {Object.entries(data.input).map(([key, value]) => (
              <div
                key={key}
                className="input-field"
                style={{
                  marginBottom: "5px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Handle
                  type="target"
                  position="left"
                  id={`${id}.input.${key}`}
                  style={{ background: "#555", marginRight: "5px" }}
                />
                <label>{key}: </label>
                <input
                  ref={(el) => (inputRefs.current[key] = el)}
                  value={value.value.fixedValue || ""}
                  onChange={(e) => handleInputChange(key, e.target.value)}
                  style={{ width: "100%", marginLeft: "5px" }}
                />
              </div>
            ))}
          </div>
          <div className="output-field">
            <h4>Output</h4>
            {Object.keys(data.output).map((key) => (
              <div
                key={key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "5px",
                }}
              >
                <span>{key}</span>
                <Handle
                  type="source"
                  position="right"
                  id={`${id}.output.${key}`}
                  style={{ background: "#555", marginLeft: "5px" }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const nodeTypes = {
  customNode: CustomNode,
};

const ReactFlowEditor = ({ initialNodeList }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    const initialNodes = initialNodeList.map((node) => ({
      id: node.id,
      type: "customNode",
      position: { x: node.index * 300, y: node.index * 100 },
      data: {
        ...node,
        input: Object.entries(node.input).reduce((acc, [key, value]) => {
          acc[key] = {
            value: value.value.contextDataField
              ? { contextDataField: value.value.contextDataField }
              : { fixedValue: value.value.fixedValue || "" },
          };
          return acc;
        }, {}),
        output: { [node.output]: true }, // Convert output to an object for consistency
      },
    }));
    setNodes(initialNodes);
  }, [initialNodeList, setNodes]);

  const onConnect = useCallback(
    (params) => {
      const sourceNode = nodes.find((node) => node.id === params.source);
      const targetNode = nodes.find((node) => node.id === params.target);
      const sourceOutputKey = params.sourceHandle.split(".")[2];
      const targetInputKey = params.targetHandle.split(".")[2];

      const updatedNodes = nodes.map((node) => {
        if (node.id === targetNode.id) {
          const updatedInput = {
            ...node.data.input,
            [targetInputKey]: {
              ...node.data.input[targetInputKey],
              value: {
                ...node.data.input[targetInputKey].value,
                contextDataField: `${sourceNode.id}.${sourceOutputKey}`,
              },
            },
          };
          return {
            ...node,
            data: {
              ...node.data,
              input: updatedInput,
            },
          };
        }
        return node;
      });

      setNodes(updatedNodes);

      const edge = {
        id: `${params.source}-${params.target}`,
        source: params.source,
        target: params.target,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        animated: true,
        style: { stroke: "#555" },
        label: `${sourceOutputKey} → ${targetInputKey}`,
      };

      setEdges((eds) => addEdge(edge, eds));
    },
    [nodes, setNodes, setEdges]
  );

  return (
    <div style={{ width: "100%", height: "800px" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        fitView
      >
        <Controls />
        <MiniMap />
        <Background variant="dots" gap={12} size={1} />
      </ReactFlow>
    </div>
  );
};

// Example usage with some nodes from the provided data
const NodeEditor = () => {
  const initialNodeList = [
    {
      id: "convertWithPrompt",
      type: "action",
      index: 2,
      input: {
        prompt: {
          value: {
            fixedValue:
              "For pickup location, convert as follows: \n{'Trans Pacific Container Services':'Trapac OAK'}, {'SSA Terminals':'OICT'},  {'Maersk Sealand - APM Terminals':'APM'}, {'West Basic Container Terminal':'WBCT'}, {'Hanjin Shipping Co, Berths': 'TTI'}, {;I.T.S.': ITS}, {'American President Lines': 'FENIX'}, {'LBCT': 'LBCT'}, {'Everport Terminal Service': 'EVERPORT'},{'Shippers Transport express': 'Shippers Transport Alameda'}, {'Yusen Terminal Inc': 'YTI'}. So for example, if the port is Yusen terminal, you would set the field to YTI.  \n\n For terminal, if picking up in Oakland set to OAK, if picking up in Los Angeles / Long beach, set to LAX. However, if the delivery address is outside of California, set to IS. For customer, if the consignee is Flexport LCL, set to Flexport BOND. If the consignee is Apple, then set to GEODIS.",
          },
        },
        terminal: {
          value: {
            fixedValue:
              "Picking up in: {{pickupLocationFormattedAddress}}, delivering to: {{deliveryLocationFormattedAddress}}",
          },
        },
        customerName: {
          value: {
            contextDataField: "consigneeName",
          },
        },
        portLocation: {
          value: {
            contextDataField: "pickupLocationFormattedAddress",
          },
        },
        consigneeName: {
          value: {
            contextDataField: "consigneeName",
          },
        },
      },
      output: "convertedDeliveryOrderData",
      lambdaPath: "/ai/processObjectwithCustomPrompt",
    },
    {
      id: "searchCustomers",
      type: "action",
      index: 3,
      input: {
        query: {
          value: {
            contextDataField: "convertedDeliveryOrderData.customerName",
          },
        },
      },
      output: "customers",
      lambdaPath: "/portpro/findCustomers",
      platformCredential: "5a4d3786-ce70-42be-bfdd-9e80d8a2fff8",
    },
    {
      id: "fuseSearchCustomers",
      type: "action",
      index: 4,
      input: {
        query: {
          value: {
            contextDataField: "convertedDeliveryOrderData.customerName",
          },
        },
        options: {
          value: {
            contextDataField: "customers",
          },
        },
        selectIndex: {
          value: {
            fixedValue: 0,
          },
        },
      },
      output: "customer",
      lambdaPath: "/ai/fuseSearch",
    },
  ];

  return <ReactFlowEditor initialNodeList={initialNodeList} />;
};

export default NodeEditor;
